import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  BackendCapabilities, ContainerSummary, CreateContainerRequest, NetworkSummary,
} from '@hub/shared';
import { api } from '../../../api/client';
import {
  ActionButton, Alert, Collapsible, EmptyRow, Loading, StateDot, fmtAgo, useFlash,
} from '../../../components/ui';
import {
  BulkBar, CheckField, DOCKER_BASE, DataTable, Field, FormActions, FormGrid, POLL_MS,
  TabToolbar, enc, errMsg, makeBulkHandler, truncStyle, useSelection, useTabResource,
  type DockerCache,
} from './shared';

type Filter = 'all' | 'running' | 'stopped' | 'hub';
type PanelKind = 'logs' | 'inspect';

const FILTERS: [Filter, string][] = [
  ['all', 'All'], ['running', 'Running'], ['stopped', 'Stopped'], ['hub', 'Hub-managed'],
];

const RESTART_POLICIES: NonNullable<CreateContainerRequest['restartPolicy']>[] =
  ['no', 'unless-stopped', 'always', 'on-failure'];

function parseLines(text: string): string[] {
  return text.split('\n').map((l) => l.trim()).filter(Boolean);
}

function parsePorts(text: string): NonNullable<CreateContainerRequest['ports']> {
  return parseLines(text).map((line) => {
    const m = /^(\d+):(\d+)(?:\/(tcp|udp))?$/.exec(line);
    if (!m) throw new Error(`Invalid port mapping: ${line}`);
    return {
      hostPort: parseInt(m[1], 10),
      containerPort: parseInt(m[2], 10),
      proto: (m[3] as 'tcp' | 'udp') || 'tcp',
    };
  });
}

function parseEnv(text: string): NonNullable<CreateContainerRequest['env']> {
  return parseLines(text).map((line) => {
    const eq = line.indexOf('=');
    if (eq < 1) throw new Error(`Invalid env line: ${line}`);
    return { key: line.slice(0, eq), value: line.slice(eq + 1) };
  });
}

function parseVolumes(text: string): NonNullable<CreateContainerRequest['volumes']> {
  return parseLines(text).map((line) => {
    const parts = line.split(':');
    if (parts.length < 2) throw new Error(`Invalid volume line: ${line}`);
    return { source: parts[0], target: parts[1], readOnly: parts[2] === 'ro' };
  });
}

function displayName(c: ContainerSummary): string {
  return c.names[0] || c.id.substring(0, 12);
}

export function ContainersTab({ caps, cache, refreshToken, prefillImage, onPrefillConsumed }: {
  caps: BackendCapabilities;
  cache: DockerCache;
  refreshToken: number;
  prefillImage: string | null;
  onPrefillConsumed: () => void;
}) {
  const { data, loading, error, reload } = useTabResource<ContainerSummary[], { containers: ContainerSummary[] }>({
    cache, tab: 'containers', path: `${DOCKER_BASE}/containers`,
    extract: (r) => r.containers, refreshToken,
  });
  const { show, node: flashNode } = useFlash();

  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [panels, setPanels] = useState<Record<string, PanelKind>>({});

  // The create form's network picker reads the networks cache.
  const cachedNetworks = cache.read<NetworkSummary[]>('networks').data;
  useEffect(() => {
    if (!caps.networks || cachedNetworks) return;
    api.get<{ networks: NetworkSummary[] }>(`${DOCKER_BASE}/networks`)
      .then((r) => cache.write('networks', r.networks))
      .catch(() => {});
  }, [caps.networks, cachedNetworks, cache]);

  const all = useMemo(() => data ?? [], [data]);
  const list = useMemo(() => all.filter((c) => {
    if (filter === 'running' && c.state !== 'running') return false;
    if (filter === 'stopped' && c.state === 'running') return false;
    if (filter === 'hub' && !c.hubManaged) return false;
    if (search) {
      const hay = `${c.names.join(' ')} ${c.image}`.toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  }), [all, filter, search]);

  const selectableIds = useMemo(
    () => list.filter((c) => !c.hubManaged).map((c) => c.id),
    [list],
  );
  const sel = useSelection(selectableIds);

  const togglePanel = useCallback((id: string, kind: PanelKind) => {
    setPanels((prev) => {
      const next = { ...prev };
      // Either button closes an open panel, matching the old row-level toggle.
      if (next[id]) delete next[id];
      else next[id] = kind;
      return next;
    });
  }, []);

  const panelCount = Object.keys(panels).length;
  const selectedCount = sel.ids.length;
  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.hidden || panelCount > 0 || selectedCount > 0) return;
      void reloadRef.current(true);
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [panelCount, selectedCount]);

  const act = useCallback(async (fn: () => Promise<unknown>) => {
    try {
      await fn();
    } catch (err) {
      show(errMsg(err), 'error');
      throw err;
    }
    await reload(true);
  }, [reload, show]);

  const bulk = (run: (id: string) => Promise<unknown>) =>
    makeBulkHandler(sel.ids, run, show, async () => { sel.clear(); await reload(true); });

  const toolbar = (
    <TabToolbar onRefresh={() => { void reload(true); }} flash={flashNode}>
      <span style={{ display: 'flex', gap: '0.25rem' }}>
        {FILTERS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            className="btn-ghost"
            style={{
              padding: '0.2rem 0.6rem',
              fontSize: '0.75rem',
              ...(filter === key ? { color: 'var(--ha-text-primary)', borderColor: 'var(--ha-accent-blue)' } : {}),
            }}
            onClick={() => setFilter(key)}
          >{label}</button>
        ))}
      </span>
      <input
        className="form-input"
        style={{ width: '14rem', padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
        placeholder="Search name or image"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <CreateContainerForm
        networks={cachedNetworks ?? []}
        prefillImage={prefillImage}
        onPrefillConsumed={onPrefillConsumed}
        onCreated={() => reload(true)}
      />
    </TabToolbar>
  );

  if (loading && !data) return <>{toolbar}<Loading /></>;
  if (error && !data) return <>{toolbar}<Alert tone="error">{error}</Alert></>;

  return (
    <>
      {toolbar}
      <BulkBar count={selectedCount}>
        <ActionButton busyLabel="Starting…" onClick={bulk((id) => api.post(`${DOCKER_BASE}/containers/${enc(id)}/start`))}>Start</ActionButton>
        <ActionButton busyLabel="Stopping…" onClick={bulk((id) => api.post(`${DOCKER_BASE}/containers/${enc(id)}/stop`))}>Stop</ActionButton>
        <ActionButton busyLabel="Restarting…" onClick={bulk((id) => api.post(`${DOCKER_BASE}/containers/${enc(id)}/restart`))}>Restart</ActionButton>
        <ActionButton
          danger
          busyLabel="Deleting…"
          confirm={`Permanently delete ${selectedCount} container(s)? This cannot be undone.`}
          onClick={bulk((id) => api.delete(`${DOCKER_BASE}/containers/${enc(id)}?force=1`))}
        >Delete</ActionButton>
      </BulkBar>

      <DataTable
        headers={[
          <input
            key="all"
            type="checkbox"
            checked={sel.allChecked}
            onChange={(e) => sel.selectAll(e.target.checked)}
          />,
          'Name', 'Image', 'State', 'Status', 'Ports', 'Created', '', 'Actions',
        ]}
      >
        {list.length === 0 && (
          <EmptyRow colSpan={9}>{all.length ? 'No containers match this filter.' : 'No containers.'}</EmptyRow>
        )}
        {list.map((c) => {
          const name = displayName(c);
          const ports = c.ports
            .map((p) => `${p.publicPort ? `${p.publicPort}:` : ''}${p.privatePort}/${p.type}`)
            .join(', ');
          return (
            <ContainerRow
              key={c.id}
              c={c}
              name={name}
              ports={ports}
              caps={caps}
              checked={sel.has(c.id)}
              onCheck={() => sel.toggle(c.id)}
              panel={panels[c.id]}
              onTogglePanel={togglePanel}
              onAct={act}
            />
          );
        })}
      </DataTable>
    </>
  );
}

function ContainerRow({ c, name, ports, caps, checked, onCheck, panel, onTogglePanel, onAct }: {
  c: ContainerSummary;
  name: string;
  ports: string;
  caps: BackendCapabilities;
  checked: boolean;
  onCheck: () => void;
  panel: PanelKind | undefined;
  onTogglePanel: (id: string, kind: PanelKind) => void;
  onAct: (fn: () => Promise<unknown>) => Promise<unknown>;
}) {
  return (
    <>
      <tr>
        <td>
          {!c.hubManaged && <input type="checkbox" checked={checked} onChange={onCheck} />}
        </td>
        <td className="mono" style={{ color: 'var(--ha-text-primary)' }}>{name}</td>
        <td className="muted" style={truncStyle('14rem')}>{c.image}</td>
        <td><StateDot state={c.state} /></td>
        <td className="muted">{c.status}</td>
        <td className="mono muted">{ports}</td>
        <td className="muted">{fmtAgo(c.created)}</td>
        <td>
          {c.hubManaged
            ? <Link className="pill pill-emerald" to="/admin" title="Managed by a hub instance">Hub instance</Link>
            : c.composeProject ? <span className="pill pill-muted">{c.composeProject}</span> : null}
        </td>
        <td style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
          {!c.hubManaged && (c.state === 'running' ? (
            <>
              <ActionButton onClick={() => onAct(() => api.post(`${DOCKER_BASE}/containers/${enc(c.id)}/stop`))}>Stop</ActionButton>
              <ActionButton onClick={() => onAct(() => api.post(`${DOCKER_BASE}/containers/${enc(c.id)}/restart`))}>Restart</ActionButton>
            </>
          ) : (
            <ActionButton onClick={() => onAct(() => api.post(`${DOCKER_BASE}/containers/${enc(c.id)}/start`))}>Start</ActionButton>
          ))}
          <button type="button" className="btn-ghost btn-sm" onClick={() => onTogglePanel(c.id, 'logs')}>Logs</button>
          <button type="button" className="btn-ghost btn-sm" onClick={() => onTogglePanel(c.id, 'inspect')}>Inspect</button>
          {caps.exec && c.state === 'running' && (
            <a
              className="btn-ghost btn-sm"
              target="_blank"
              rel="noreferrer"
              href={`/admin/docker/containers/${enc(c.id)}/exec?name=${enc(name)}`}
            >Console</a>
          )}
          {c.hubManaged
            ? <Link className="btn-ghost btn-sm" to="/admin">Manage →</Link>
            : (
              <ActionButton
                danger
                busyLabel="Deleting…"
                confirm="Permanently delete this container? This cannot be undone."
                onClick={() => onAct(() => api.delete(`${DOCKER_BASE}/containers/${enc(c.id)}?force=1`))}
              >Delete</ActionButton>
            )}
        </td>
      </tr>
      {panel && (
        <tr>
          <td colSpan={9} style={{ padding: '0 0.4rem 0.75rem' }}>
            <ContainerPanel id={c.id} kind={panel} />
          </td>
        </tr>
      )}
    </>
  );
}

function ContainerPanel({ id, kind }: { id: string; kind: PanelKind }) {
  const [text, setText] = useState('Loading…');

  useEffect(() => {
    let cancelled = false;
    const load = kind === 'logs'
      ? api.get<{ logs: string }>(`${DOCKER_BASE}/containers/${enc(id)}/logs?tail=500`)
        .then((r) => r.logs || '(no output)')
      : api.get<{ inspect: unknown }>(`${DOCKER_BASE}/containers/${enc(id)}`)
        .then((r) => JSON.stringify(r.inspect, null, 2));
    load.then((t) => { if (!cancelled) setText(t); })
      .catch((err: unknown) => { if (!cancelled) setText(errMsg(err)); });
    return () => { cancelled = true; };
  }, [id, kind]);

  return <pre className="output">{text}</pre>;
}

function CreateContainerForm({ networks, prefillImage, onPrefillConsumed, onCreated }: {
  networks: NetworkSummary[];
  prefillImage: string | null;
  onPrefillConsumed: () => void;
  onCreated: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [image, setImage] = useState('');
  const [name, setName] = useState('');
  const [portsText, setPortsText] = useState('');
  const [envText, setEnvText] = useState('');
  const [volumesText, setVolumesText] = useState('');
  const [network, setNetwork] = useState('');
  const [restart, setRestart] = useState<NonNullable<CreateContainerRequest['restartPolicy']>>('no');
  const [startNow, setStartNow] = useState(true);
  const { show, node } = useFlash();
  const imageRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!prefillImage) return;
    setOpen(true);
    setImage(prefillImage);
    onPrefillConsumed();
  }, [prefillImage, onPrefillConsumed]);

  useEffect(() => {
    if (open) imageRef.current?.focus();
  }, [open]);

  const submit = async () => {
    let spec: CreateContainerRequest;
    try {
      const trimmedImage = image.trim();
      if (!trimmedImage) throw new Error('Image is required');
      spec = {
        image: trimmedImage,
        ports: parsePorts(portsText),
        env: parseEnv(envText),
        volumes: parseVolumes(volumesText),
        restartPolicy: restart,
        start: startNow,
      };
      if (name.trim()) spec.name = name.trim();
      if (network) spec.network = network;
    } catch (err) {
      show(errMsg(err), 'error');
      throw err;
    }

    try {
      const res = await api.post<{ id: string; warnings?: string[] }>(`${DOCKER_BASE}/containers`, spec);
      if (res.warnings?.length) show(res.warnings.join(' '), 'warn');
    } catch (err) {
      show(errMsg(err), 'error');
      throw err;
    }
    await onCreated();
  };

  return (
    <Collapsible label="＋ Create container" open={open} onToggle={() => setOpen((v) => !v)}>
      <FormGrid>
        <Field label="Image (required)">
          <input ref={imageRef} className="form-input" placeholder="nginx:alpine" value={image} onChange={(e) => setImage(e.target.value)} />
        </Field>
        <Field label="Name">
          <input className="form-input" placeholder="my-container" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Ports (host:container[/proto], one per line)">
          <textarea className="form-input" rows={3} placeholder="8080:80" value={portsText} onChange={(e) => setPortsText(e.target.value)} />
        </Field>
        <Field label="Env (KEY=VALUE, one per line)">
          <textarea className="form-input" rows={3} value={envText} onChange={(e) => setEnvText(e.target.value)} />
        </Field>
        <Field label="Volumes (source:/path[:ro], one per line)">
          <textarea className="form-input" rows={3} value={volumesText} onChange={(e) => setVolumesText(e.target.value)} />
        </Field>
        <Field label="Network">
          <select className="form-input" value={network} onChange={(e) => setNetwork(e.target.value)}>
            <option value="">(default)</option>
            {networks.map((n) => <option key={n.id} value={n.name}>{n.name}</option>)}
          </select>
        </Field>
        <Field label="Restart policy">
          <select
            className="form-input"
            value={restart}
            onChange={(e) => setRestart(e.target.value as NonNullable<CreateContainerRequest['restartPolicy']>)}
          >
            {RESTART_POLICIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
        <CheckField label="Start immediately" checked={startNow} onChange={setStartNow} />
      </FormGrid>
      <FormActions>
        <ActionButton className="btn-primary" busyLabel="Creating…" onClick={submit}>Create</ActionButton>
        {node}
      </FormActions>
    </Collapsible>
  );
}
